import { useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, Shield, ArrowRight, Loader2 } from "lucide-react";
import { recordConsent, CONSENT_VERSION } from "../lib/api";
import { useAuth } from "../lib/auth-context";

/**
 * ConsentGate — renders a full-screen modal that blocks app usage until the
 * authenticated user accepts the current Terms of Service and Privacy Policy.
 *
 * Usage: wrap protected content in App.js or ProtectedRoute. If
 * `user.consent_v1_at` is falsy (user has never accepted, or a new version
 * was published), this gate renders instead of children.
 *
 * On accept: calls POST /me/consent, refreshes user context, then renders
 * children on the next render cycle.
 */
export default function ConsentGate({ children }) {
  const { user, refresh } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Only gate authenticated users who haven't consented to the CURRENT version
  if (!user || typeof user !== "object") return children;
  if (user.consent_v1_at && user.consent_v1_version === CONSENT_VERSION) return children;

  const onAccept = async () => {
    if (!accepted) {
      setError("You must accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await recordConsent(CONSENT_VERSION);
      await refresh();
    } catch (err) {
      setError("Something went wrong recording your consent. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-[#050508] grid place-items-center px-4 py-10" data-testid="consent-gate">
      <div className="w-full max-w-lg sr-glass rounded-2xl p-8 sr-fade-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center">
            <Disc3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Full Spectrum Center</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Legal Agreement</div>
          </div>
        </div>

        <h1 className="font-display text-2xl font-black tracking-tight mb-2">
          Terms &amp; Privacy Update
        </h1>
        <p className="text-sm text-white/60 mb-6">
          Before you continue, please review and accept our Terms of Service and Privacy Policy.
          These documents explain how Full Spectrum Center works, what data we collect, and your rights.
        </p>

        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6 space-y-3">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#00F0FF] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-white">What you're agreeing to:</div>
              <ul className="text-xs text-white/60 mt-1.5 space-y-1 list-disc pl-4">
                <li>You are 18 years or older</li>
                <li>You accept responsibility for content you broadcast (including music licensing)</li>
                <li>You consent to the data collection described in our Privacy Policy</li>
                <li>You agree to follow community guidelines and moderation rules</li>
                <li>You understand that automated bots may moderate chat and camera behavior</li>
              </ul>
            </div>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none mb-4" data-testid="consent-gate-checkbox-row">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#FF007F] cursor-pointer shrink-0"
            data-testid="consent-gate-checkbox"
          />
          <span className="text-xs text-white/70 leading-snug">
            I am 18+ and I accept Full Spectrum Center's{" "}
            <Link to="/legal/terms" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="consent-gate-terms-link">
              Terms of Service
            </Link>
            {" "}and{" "}
            <Link to="/legal/privacy" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="consent-gate-privacy-link">
              Privacy Policy
            </Link>.
          </span>
        </label>

        {error && (
          <p className="text-sm text-[#FF007F] font-medium mb-4" data-testid="consent-gate-error">
            {error}
          </p>
        )}

        <button
          onClick={onAccept}
          disabled={!accepted || busy}
          className="w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg px-6 py-3.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between"
          data-testid="consent-gate-accept-btn"
        >
          <span className="flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Accept &amp; Continue
          </span>
          <ArrowRight className="w-4 h-4" />
        </button>

        <p className="mt-4 text-center text-[11px] text-white/40">
          Version {CONSENT_VERSION} &middot; Effective June 22, 2026
        </p>
      </div>
    </div>
  );
}
